import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Layout from "../Components/Layout";
import { fetchUserSubscriptions, fetchTransactionsByCustomer, payTransaction, fetchTransactionsBySubscription, generateSubscriptionStatement, fetchSubscriptionCountdown, rechargeSubscription } from "../apiService";
import { Subscription, Transaction } from "../Interface";
import { useNavigate } from "react-router-dom"; 
import '../App.css';
import { useGlobalError } from '../Components/GlobalErrorContext';
import { useGlobalSuccess } from '../Components/GlobalSuccessContext';
import { OverlayTrigger, Popover } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import './ManageSubscription.css';


const ManageSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [activeTab, setActiveTab] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false); // State to control payment modal
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null); // Store selected transaction
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null); // Store selected payment method
  const [tokenise, setTokenise] = useState<boolean>(false); // State for tokenization checkbox
  const { showErrorModal } = useGlobalError();
  const { showSuccessModal } = useGlobalSuccess();

  // Countdown state
  const [countdown, setCountdown] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
  const [countdownVisible, setCountdownVisible] = useState<boolean>(false);

  const portalEndCustomerId = localStorage.getItem("portalEndCustomerId");

  // ✅ Popover content for subscription status info
  const getStatusInfo = (status: string): string => {
    const infoMap: Record<string, string> = {
      pending_payment: "PENDING PAYMENT — The installation of your new fibre line is pending payment, please use the pay now option to make payment.", 
      active: "ACTIVE — Your fibre line is paid for the current cycle and is activated.",
      pending_documents: "PENDING DOCUMENTS — Please upload your RICA documents, this is needed to activate service or schedule installation.",
      suspended: "SUSPENDED — Your fibre line has been suspended, please use the pay now option to enjoy a new 30-day cycle.",
      cancelled: "CANCELLED — Your fibre line has been cancelled.",
      pending_installation: "PENDING INSTALLATION — Your fibre line needs to be installed, please contact support at 0861777889 or support@gplat.co.za if not already scheduled with you.",
    };
    return infoMap[status] || "Status information not available.";
  };

  useEffect(() => {
    if (portalEndCustomerId) {
      Promise.all([
        fetchUserSubscriptions(portalEndCustomerId),
        fetchTransactionsByCustomer(Number(portalEndCustomerId)),
      ])
        .then(([subscriptionsData, transactionsData]) => {
          setSubscriptions(subscriptionsData);
          setTransactions(transactionsData);
          if (subscriptionsData.length > 0) {
            setActiveTab(subscriptionsData[0].portalProduct?.name || "");
          }
          setLoading(false);
        })
        .catch((err) => {
          setError("Error fetching subscription or transaction details");
          setLoading(false);
        });
    }
  }, [portalEndCustomerId]);

  // Fetch transactions for the active subscription when the active tab changes
  useEffect(() => {
    if (activeTab) {
      const activeSubscription = subscriptions.find(
        (sub) => sub.portalProduct?.name === activeTab
      );
  
      if (activeSubscription) {
        fetchTransactionsBySubscription(activeSubscription.id)
          .then((transactionsData) => {
            setTransactions(transactionsData); // Update transactions state
          })
          .catch((err) => {
            setError("Error fetching transactions for the selected subscription");
          });
      }
    }
  }, [activeTab, subscriptions]);

const handleProceed = async () => {
  if (!selectedPaymentMethod) {
    alert("Please select a payment method before proceeding.");
    return;
  }

  if (!selectedTransaction?.id) {
    alert("Transaction details not found.");
    return;
  }

  if (!portalEndCustomerId) {
    alert("Customer details not found. Please log in again.");
    return;
  }

  try {
    // ✅ Always use /pay endpoint to get the redirect (ignore rechargePaymentRedirect)
    const res = await payTransaction(
      portalEndCustomerId,
      selectedTransaction.id,
      selectedPaymentMethod as any,
      selectedPaymentMethod === "creditcard" && tokenise
    );

    if (!res.success || !res.paymentRedirect) {
      alert(res.message || "Payment failed. Please try again.");
      return;
    }

    setShowPaymentModal(false);

    // ✅ Redirect to PayFast
    window.location.href = res.paymentRedirect;
  } catch (error) {
    console.error(error);
    alert("Error processing the payment. Please try again.");
  }
};


  const handleCancel = () => {
    setShowPaymentModal(false); // Close modal on cancel
  };

  const handleManageClick = (subscriptionId: number) => {
    navigate(`/manage-package/${subscriptionId}`); // Redirect to the ManagePackage page
  };

  const handlePayClick = async (subscription: Subscription) => {
    try {
      console.log("Starting recharge for subscription:", subscription);
      
      const result = await rechargeSubscription(subscription.id);

      if (!result.success) {
        showErrorModal(result.message || "Failed to recharge subscription");
        return;
      }

      // ✅ Fetch transactions for the subscription to get the actual transaction created by recharge
      const transactionsData = await fetchTransactionsBySubscription(subscription.id);
      
      // ✅ Find the most recent pending transaction (created by recharge)
      const rechargeTransaction = transactionsData.find(
        (t: Transaction) => t.status === "pending" && t.transactionType === "credit"
      );

      if (!rechargeTransaction) {
        // ✅ If no pending transaction found, try to find any pending transaction
        const anyPendingTransaction = transactionsData.find(
          (t: Transaction) => t.status === "pending"
        );
        
        if (anyPendingTransaction) {
          setSelectedTransaction(anyPendingTransaction);
          setShowPaymentModal(true);
          setTokenise(false);
          setSelectedPaymentMethod(null);
          return;
        }
        
        showErrorModal("Transaction created for recharge was not found");
        return;
      }

      // ✅ Set recharge flow state with transaction data
      setSelectedTransaction(rechargeTransaction);
      setShowPaymentModal(true);
      setTokenise(false);
      setSelectedPaymentMethod(null);
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.detail || (error as any)?.response?.data?.message || (error as any)?.message || "Error initiating subscription recharge";
      showErrorModal(errorMessage);
      console.error(error);
    }
  };



const handleDownloadStatement = async () => {
  const subscription = subscriptions[0]; // ✅ safely grab the first item

  if (!subscription) {
    showErrorModal("No subscription found.");
    return;
  }

  const result = await generateSubscriptionStatement(subscription.id);

  if (result.success) {
    const url = window.URL.createObjectURL(new Blob([result.blob], { type: result.contentType }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", result.filename || "statement.pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // ✅ Show success modal
    showSuccessModal("Statement downloaded successfully.");
  } else {
    showErrorModal(result.message);
  }
};



useEffect(() => {
  const subscriptionId = subscriptions[0]?.id;
  if (!subscriptionId) {
    setCountdown(null);
    setCountdownVisible(false);
    return;
  }

  let mounted = true;
  let intervalId: number | null = null;

  (async () => {
    try {
      const res = await fetchSubscriptionCountdown(subscriptionId);
      if (!mounted) return;

      // Extract the next billing date from the API response
      const nextBillingDate = res?.data?.next_billing_date;

      if (!nextBillingDate) {
        setCountdown(null);
        setCountdownVisible(false);
        return;
      }

      const targetTime = new Date(nextBillingDate).getTime();
      const update = () => {
        const now = Date.now();
        const diff = targetTime - now;
        if (diff <= 0) {
          setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          setCountdownVisible(false);
          if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }
        const seconds = Math.floor((diff / 1000) % 60);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        setCountdown({ days, hours, minutes, seconds });
        setCountdownVisible(true);
      };

      update();
      intervalId = window.setInterval(update, 1000);
    } catch (err) {
      console.error("Countdown fetch error:", err);
      setCountdown(null);
      setCountdownVisible(false);
    }
  })();

  return () => {
    mounted = false;
    if (intervalId !== null) clearInterval(intervalId);
  };
}, [subscriptions]);


  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  if (error) return <p className="text-danger">{error}</p>;

  return (
    <Layout>
      <div className="container my-2">
        <div className="d-flex mb-3">
        </div>
  {/* Countdown UI (before subscriptions heading) */}
  {countdownVisible && countdown && (
    <div className="mb-2 p-2 rounded" style={{ backgroundColor: "#FFF5EE", color: "#E2520F", fontWeight: 600, fontSize: "1.5rem" }}>
      Next Account Payment Due in:&nbsp;
      {countdown.days}d&nbsp;
      {String(countdown.hours).padStart(2, "0")}:
      {String(countdown.minutes).padStart(2, "0")}:
      {String(countdown.seconds).padStart(2, "0")}
    </div>
  )}
  <br/>
        <div className="mb-4 p-1 border rounded shadow-sm">
  <h4 className="mb-3 text-start" style={{ color: "#E2520F", fontWeight: "bold"}}>SUBSCRIPTIONS</h4>
  <hr/>
  <div style={{ overflowX: "visible" }}>
    {subscriptions.length > 0 ? (
      subscriptions.map((subscription) => (
        <table className="table table-bordered" key={subscription.reference} style={{ marginBottom: "2rem", borderColor: "#333", borderWidth: "2px" }}>
          <tbody>
            <tr>
              <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Plan</th>
              <td style={{ borderColor: "#333", borderWidth: "1px" }}>{subscription.portalProduct?.name}</td>
            </tr>
            <tr>
              <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Pay Date</th>
              <td style={{ borderColor: "#333", borderWidth: "1px" }}>{new Date(subscription.nextBillingDate).toLocaleDateString()}</td>
            </tr>
            <tr>
              <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Status</th>
              <td className="align-middle" style={{ borderColor: "#333", borderWidth: "1px" }}>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className={
                      subscription.status === "active"
                        ? "text-success fw-medium"
                        : "text-danger fw-medium"
                    }
                  >
                    {subscription.status.replace("_", " ").toUpperCase()}
                  </span>

                  <OverlayTrigger
                    trigger="click"
                    placement="auto"
                    rootClose
                    overlay={
                      <Popover id={`popover-${subscription.id}`}>
                        <Popover.Header as="h3">Status Info</Popover.Header>
                        <Popover.Body>{getStatusInfo(subscription.status)}</Popover.Body>
                      </Popover>
                    }
                  >
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted"
                      title="More info"
                      style={{ fontSize: '1rem' }}
                    >
                      <InfoCircle size={16} />
                    </button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ borderColor: "#333", borderWidth: "1px" }}>
                <div className="compact-actions" style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                  <button
                    className="btn btn-sm btn-outline-primary "
                    style={{ flex: "0 0 auto", minWidth: 0}}
                    onClick={() => handleManageClick(subscription.id)}
                  >
                    MANAGE
                  </button>
                  <span>|</span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ flex: "0 0 auto", minWidth: 0 }}
                    onClick={() => handleDownloadStatement()}
                  >
                    STATEMENT 📥
                  </button>
                  <span>|</span>
                  <button
                    className="btn btn-sm btn-outline-success"
                    style={{ flex: "0 0 auto", minWidth: 0 }}
                    onClick={() => handlePayClick(subscription)}
                  >
                    PAY ACCOUNT
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      ))
    ) : (
      <p className="text-center">No subscriptions found.</p>
    )}
  </div>
</div>


        <div className="p-1 border rounded shadow-sm">
          <h4 className="mb-3" style={{ color: "#E2520F", fontWeight: "bold"}}>TRANSACTIONS</h4>
          <hr/>
          <ul className="nav nav-tabs mb-3">
            {subscriptions.map((subscription) => (
              <li className="nav-item" key={subscription.reference}>
                <button
                  className={`nav-link ${activeTab === subscription.portalProduct?.name ? "active" : ""}`}
                  onClick={() => setActiveTab(subscription.portalProduct?.name || "")}
                >
                  {subscription.portalProduct?.name}
                </button>
              </li>
            ))}
          </ul>

          <div style={{ overflowX: "auto" }}>
            {Array.isArray(transactions) && transactions.filter(t => t.transactionType === "credit")
              .length > 0 ? (
                transactions
                  .filter(t => t.transactionType === "credit")
                  .map((transaction, index) => (
                    <table className="table table-bordered" key={index} style={{ marginBottom: "2rem", borderColor: "#333", borderWidth: "2px" }}>
                      <tbody>
                        <tr>
                          <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Date</th>
                          <td style={{ borderColor: "#333", borderWidth: "1px" }}>{new Date(transaction.transactionDate).toLocaleDateString()}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Description</th>
                          <td style={{ borderColor: "#333", borderWidth: "1px" }}>{transaction.description}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Amount</th>
                          <td style={{ borderColor: "#333", borderWidth: "1px" }}>R{transaction.amountIncl}.00 {transaction.currency}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "30%", borderColor: "#333", borderWidth: "1px" }}>Status</th>
                          <td style={{ 
                            borderColor: "#333", 
                            borderWidth: "1px",
                            color: transaction.status === "pending" ? "red" : transaction.status === "completed" ? "green" : "black"
                          }}>{String(transaction.status).replace(/_/g, " ").toUpperCase()}</td>
                        </tr>
                        <tr>
                          {/* <td colSpan={2} style={{ borderColor: "#333", borderWidth: "1px" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                              {transaction.status === "pending" && (
                                <button
                                  className="btn-action"
                                  onClick={() => handlePayNowClick(transaction)}
                                >
                                  Pay Now
                                </button>
                              )}
                            </div>
                          </td> */}
                        </tr>
                      </tbody>
                    </table>
                  ))
              ) : (
                <p className="text-center">
                  No credit card transactions found.
                </p>
              )}
          </div>
        </div>
      </div>
    <br/>
    <br/>
    <br/>
    <br/>
    
      {/* Payment Modal */}
      {showPaymentModal && selectedTransaction && (
        <div className="modal show d-block" tabIndex={-1} role="dialog" >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#e2520f', color: 'white' }}>
                <h5 className="modal-title">Select Payment Method</h5>
                <button type="button" className="btn-lg" onClick={handleCancel} style={{ backgroundColor: '#e2520f', color: 'white', border: 'none' }}>
                  <i className="bi bi-credit-card"></i>
                </button>

              </div>
              <div className="modal-body">
                <h6>Transaction Reference: {selectedTransaction.reference}</h6>
                <p>Amount: {selectedTransaction.amountIncl} {selectedTransaction.currency}</p>
                <div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="cash"
                      name="PaymentMethod"
                      value="cash"
                      checked={selectedPaymentMethod === "cash"}
                      onChange={() => setSelectedPaymentMethod("cash")}
                      className="form-check-input"
                    />
                    <label className="form-check-label" htmlFor="cash">Cash (Mukuru)</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="eft"
                      name="PaymentMethod"
                      value="eft"
                      checked={selectedPaymentMethod === "eft"}
                      onChange={() => setSelectedPaymentMethod("eft")}
                      className="form-check-input"
                    />
                    <label className="form-check-label" htmlFor="eft">EFT / Bank Transfer</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="creditcard"
                      name="PaymentMethod"
                      value="creditcard"
                      checked={selectedPaymentMethod === "creditcard"}
                      onChange={() => setSelectedPaymentMethod("creditcard")}
                      className="form-check-input"
                    />
                    <label className="form-check-label" htmlFor="creditcard">Debit Card/ Credit Card</label>
                  </div>
                  {/* <div className="form-check">
                    <input
                      type="radio"
                      id="capitecpay"
                      name="PaymentMethod"
                      value="capitecpay"
                      checked={selectedPaymentMethod === "capitecpay"}
                      onChange={() => setSelectedPaymentMethod("capitecpay")}
                      className="form-check-input"
                    />
                    <label className="form-check-label" htmlFor="capitecpay">Capitec Pay</label>
                  </div> */}
                </div>
              {/* Dynamically show the checkbox for tokenization if Credit Card is selected */}
              {/* credit‑card specific checkbox */}
              {selectedPaymentMethod === "creditcard" && (
                <div className="form-check mt-3 d-flex align-items-center">
                  <input
                    type="checkbox"
                    id="tokenize_card"
                    className="form-check-input"
                    checked={tokenise}
                    onChange={e => setTokenise(e.target.checked)}   // ⬅️ bind state
                  />
                  <label htmlFor="tokenize_card" className="form-check-label ms-2">
                    I authorise the portal to <strong>debit</strong> my card for future payments.
                  </label>
                </div>
              )}

              </div>
              
              <div className="modal-footer d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleProceed}
                  style={{ backgroundColor: '#e2520f', borderColor: '#e2520f' }}
                >
                  Proceed
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default ManageSubscription;

